import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import { ROUTES } from "@routing/routes";
import { Button, Icon, Title } from "@common/index";
import { ComponentColor } from "@common/types";
import { useAuth } from "@/hooks/useAuth";
import { signInRedirect } from "@components/Auth/signInRedirect";
import { useApplicationApi } from "@/hooks/apiHooks/useApplicationApi";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import { isProcessingApplicationAtom } from "@/store/applicationAtoms";
import {
    MAX_FILE_BYTES,
    ACCEPTED_FILE_TYPES,
    CERTIFICATE_REQUIRED_MESSAGE,
    CERTIFICATE_SIZE_MESSAGE,
    CERTIFICATE_TYPE_MESSAGE,
} from "@utils/certificateFile";
import { US_STATES } from "@utils/usStates";
import { formatUsPhone } from "./_helpers/formatUsPhone";
import { applicationErrorMessage } from "./_helpers/applicationErrorMessage";
import { captchaErrorMessage } from "./_helpers/captchaErrorMessage";
import { useTurnstile } from "./_helpers/useTurnstile";
import { ApplyTextField } from "./_components/ApplyTextField";
import { TurnstileCheck } from "./_components/TurnstileCheck";

// File-upload rules and copy live in @utils/certificateFile — shared with the
// per-location resubmit flow on the pending wall (#198).

// US-only marketplace: the legal-class map (§3.12) is keyed by US state and zip.
const COUNTRY_CODE = "US";

// Mirrors DomainConstraint on the API side, so a length the server would reject
// with a 400 is caught inline instead. Phone (32), state (128), country (128)
// and zip (16) are omitted: the mask, the two selects and the zip pattern
// already pin those well inside their limits, so a rule could never fire.
const MAX = {
    firstName: 128,
    lastName: 128,
    email: 256,
    legalBusinessName: 256,
    taxId: 64,
    streetAddress: 256,
    city: 128,
} as const;

const applicationSchema = z.object({
    firstName: z
        .string()
        .trim()
        .nonempty("First name is required")
        .max(MAX.firstName, `First name must be ${MAX.firstName} characters or fewer`),
    lastName: z
        .string()
        .trim()
        .nonempty("Last name is required")
        .max(MAX.lastName, `Last name must be ${MAX.lastName} characters or fewer`),
    email: z
        .string()
        .trim()
        .nonempty("Email address is required")
        .email("Enter a valid email address")
        .max(MAX.email, `Email address must be ${MAX.email} characters or fewer`),
    phoneNumber: z
        .string()
        .trim()
        .nonempty("Phone number is required")
        .regex(
            /^\(\d{3}\) \d{3}-\d{4}$/,
            "Enter a valid 10-digit phone number",
        ),
    legalBusinessName: z
        .string()
        .trim()
        .nonempty("Legal business name is required")
        .max(
            MAX.legalBusinessName,
            `Legal business name must be ${MAX.legalBusinessName} characters or fewer`,
        ),
    taxId: z
        .string()
        .trim()
        .nonempty("Tax ID is required")
        .max(MAX.taxId, `Tax ID must be ${MAX.taxId} characters or fewer`),
    // The application creates the organization's first Location (DecisionLog
    // §1.2), which needs an address — so every address field is required.
    streetAddress: z
        .string()
        .trim()
        .nonempty("Street address is required")
        .max(
            MAX.streetAddress,
            `Street address must be ${MAX.streetAddress} characters or fewer`,
        ),
    city: z
        .string()
        .trim()
        .nonempty("City is required")
        .max(MAX.city, `City must be ${MAX.city} characters or fewer`),
    // Must be the 2-letter USPS code, uppercase. LegalClassRestriction rows are
    // stored that way and matched exactly, and §3.12 treats "no match" as
    // allowed — so a full state name here would silently disable the
    // restriction rather than fail loudly. The select can only emit codes; this
    // rule is the invariant that keeps it that way.
    state: z
        .string()
        .nonempty("State is required")
        .regex(/^[A-Z]{2}$/, "Select a state from the list"),
    // ZIP+4 is deliberately allowed: the API's NormalizeZipCode takes the
    // 5-digit prefix before the "-", so "11220-1234" already matches the
    // "11220" restriction row. What this closes is the MALFORMED case — a
    // typo'd or non-US postcode matches no row at all, and §3.12 reads "no
    // match" as allowed. The value is posted as entered; normalizing it here
    // would only mask a future divergence from the server's normalizer.
    // No max rule: anything matching this pattern is at most 10 characters, so
    // the server's 16 could never be exceeded.
    zipCode: z
        .string()
        .trim()
        .nonempty("Zip code is required")
        .regex(
            /^\d{5}(-\d{4})?$/,
            "Enter a 5-digit ZIP, optionally ZIP+4",
        ),
    // Now that this error is rendered, it needs human copy — zod's default for
    // a literal mismatch reads `Invalid input: expected "US"`.
    country: z.literal(COUNTRY_CODE, {
        message: "We currently accept applications from the United States only",
    }),
    resaleCertificate: z
        .custom<FileList>()
        .refine(
            (files) => files && files.length > 0,
            CERTIFICATE_REQUIRED_MESSAGE,
        )
        .refine(
            (files) => !files?.[0] || files[0].size <= MAX_FILE_BYTES,
            CERTIFICATE_SIZE_MESSAGE,
        )
        .refine(
            (files) =>
                !files?.[0] || ACCEPTED_FILE_TYPES.includes(files[0].type),
            CERTIFICATE_TYPE_MESSAGE,
        ),
});

type ApplicationFormData = z.infer<typeof applicationSchema>;

const benefits = [
    "Best prices in the industry",
    "Earn rewards",
    "Fast shipping",
];

export const ApplyPage = () => {
    const navigate = useNavigate();
    const { instance } = useAuth();
    const { submitApplication } = useApplicationApi();
    const isProcessing = useAtomValue(isProcessingApplicationAtom);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const turnstile = useTurnstile();
    const runGuarded = useSubmitGuard();

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<ApplicationFormData>({
        resolver: zodResolver(applicationSchema),
        // The two <select> fields need their initial value declared here — the
        // state placeholder is empty so it fails "required" until chosen, and
        // country is fixed.
        defaultValues: { state: "", country: COUNTRY_CODE },
    });

    const handleSignIn = () => {
        signInRedirect(instance);
    };

    // A double-click (or a double Enter) lands twice before the processing
    // flag disables Submit: the guard sends one application and spends one
    // Turnstile token.
    const onSubmit = (data: ApplicationFormData) =>
        runGuarded(async () => {
            setSubmitError(null);

            try {
                const response = await submitApplication({
                    firstName: data.firstName.trim(),
                    lastName: data.lastName.trim(),
                    email: data.email.trim(),
                    phoneNumber: data.phoneNumber.trim(),
                    legalBusinessName: data.legalBusinessName.trim(),
                    taxId: data.taxId.trim(),
                    streetAddress: data.streetAddress.trim(),
                    city: data.city.trim(),
                    // Already exact values from their <select> — no trimming or
                    // case-fixing, which would only hide a broken option list.
                    state: data.state,
                    country: data.country,
                    zipCode: data.zipCode.trim(),
                    resaleCertificate: data.resaleCertificate[0],
                    turnstileToken: turnstile.token,
                });

                // The 202 carries the destination; the local route is the fallback
                // for a response that arrives without one. A known email gets this
                // same 202 (OB-12).
                navigate(response.redirectUrl ?? ROUTES.APPLY_SUCCESS);
            } catch (error) {
                setSubmitError(
                    captchaErrorMessage(error) ?? applicationErrorMessage(error),
                );
                // The refused request spent the token; the next submit needs a
                // fresh one. (A success navigates away, so it needs no reset.)
                turnstile.reset();
            }
        });

    return (
        <div className="max-w-2xl mx-auto px-4 py-10 text-primary">
            <Title>Apply for an account.</Title>

            <p className="text-base mt-6">
                Creating an account gives you access to the best selection of
                premium Cannabis, CBD, Kratom, Vapes and more:
            </p>
            <ul className="mt-4 space-y-2">
                {benefits.map((benefit) => (
                    <li key={benefit} className="flex items-center gap-2">
                        <Icon name="check" className="text-xl" />
                        <span className="text-base">{benefit}</span>
                    </li>
                ))}
            </ul>

            <form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                className="mt-8 rounded-2xl bg-base-200 p-6 sm:p-8 space-y-6"
            >
                <div className="grid gap-6 sm:grid-cols-2">
                    <ApplyTextField
                        id="firstName"
                        label="First Name"
                        registration={register("firstName")}
                        error={errors.firstName?.message}
                    />
                    <ApplyTextField
                        id="lastName"
                        label="Last Name"
                        registration={register("lastName")}
                        error={errors.lastName?.message}
                    />
                </div>

                <div>
                    <label htmlFor="email" className="block font-bold mb-2">
                        Email Address
                    </label>
                    <label className="input input-bordered w-full bg-white text-neutral flex items-center gap-2">
                        <Icon name="mail" className="text-lg opacity-60" />
                        <input
                            id="email"
                            type="email"
                            placeholder="mail@site.com"
                            className="grow"
                            {...register("email")}
                        />
                    </label>
                    {errors.email && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.email.message}
                        </p>
                    )}
                </div>

                <div>
                    <label
                        htmlFor="phoneNumber"
                        className="block font-bold mb-2"
                    >
                        Phone Number
                    </label>
                    <input
                        id="phoneNumber"
                        type="tel"
                        inputMode="numeric"
                        placeholder="(___) ___-____"
                        className="input input-bordered w-full bg-white text-neutral"
                        {...register("phoneNumber")}
                        onChange={(e) =>
                            setValue(
                                "phoneNumber",
                                formatUsPhone(e.target.value),
                                { shouldValidate: true },
                            )
                        }
                    />
                    {errors.phoneNumber && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.phoneNumber.message}
                        </p>
                    )}
                </div>

                <ApplyTextField
                    id="legalBusinessName"
                    label="Legal Business Name (as shown on your tax documents)"
                    registration={register("legalBusinessName")}
                    error={errors.legalBusinessName?.message}
                />

                <ApplyTextField
                    id="taxId"
                    label="Tax ID"
                    registration={register("taxId")}
                    error={errors.taxId?.message}
                />

                <ApplyTextField
                    id="streetAddress"
                    label="Street Address"
                    registration={register("streetAddress")}
                    error={errors.streetAddress?.message}
                />

                <div className="grid gap-6 sm:grid-cols-3">
                    <ApplyTextField
                        id="city"
                        label="City"
                        registration={register("city")}
                        error={errors.city?.message}
                    />
                    <div>
                        <label
                            htmlFor="state"
                            className="block font-bold mb-2"
                        >
                            State
                        </label>
                        {/* A select, not a text input: the submitted value has
                            to be the 2-letter USPS code for the legal-class
                            restrictions to match at all. */}
                        <select
                            id="state"
                            className="select select-bordered w-full bg-white text-neutral"
                            {...register("state")}
                        >
                            <option value="">Select a state</option>
                            {US_STATES.map((state) => (
                                <option key={state.code} value={state.code}>
                                    {state.name}
                                </option>
                            ))}
                        </select>
                        {errors.state && (
                            <p className="text-red-500 text-sm mt-1">
                                {errors.state.message}
                            </p>
                        )}
                    </div>
                    <ApplyTextField
                        id="zipCode"
                        label="Zip Code"
                        registration={register("zipCode")}
                        error={errors.zipCode?.message}
                    />
                </div>

                <div>
                    <label htmlFor="country" className="block font-bold mb-2">
                        Country
                    </label>
                    {/* Fixed: US-only marketplace. Shown rather than hidden so
                        the applicant can see the address they are submitting. */}
                    <select
                        id="country"
                        className="select select-bordered w-full bg-white text-neutral"
                        {...register("country")}
                    >
                        <option value={COUNTRY_CODE}>United States</option>
                    </select>
                    {errors.country && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.country.message}
                        </p>
                    )}
                </div>

                <div>
                    <label
                        htmlFor="resaleCertificate"
                        className="block font-bold mb-2"
                    >
                        Upload Resale Certificate
                    </label>
                    <input
                        id="resaleCertificate"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="file-input file-input-bordered w-full bg-white text-neutral"
                        {...register("resaleCertificate")}
                    />
                    <p className="text-xs text-neutral/60 mt-1">
                        PDF, JPG, or PNG, up to 10 MB.
                    </p>
                    {errors.resaleCertificate && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.resaleCertificate.message as string}
                        </p>
                    )}
                </div>

                <TurnstileCheck turnstile={turnstile} />

                {submitError && (
                    <div role="alert" className="alert alert-error">
                        <span>{submitError}</span>
                    </div>
                )}

                <Button
                    type="submit"
                    color={ComponentColor.Warning}
                    loading={isProcessing}
                    disabled={turnstile.isAwaitingToken}
                    aria-describedby={
                        turnstile.isAwaitingToken ? turnstile.hintId : undefined
                    }
                    className="text-neutral font-bold normal-case"
                >
                    Submit Application
                </Button>
            </form>

            <p className="mt-6 text-base">
                Already have an account?{" "}
                <button
                    type="button"
                    id="apply-signin-button"
                    onClick={handleSignIn}
                    className="link link-primary font-bold normal-case"
                >
                    Sign In
                </button>
            </p>
        </div>
    );
};
